import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';

import { AppShell } from '@/components/layout/AppShell';
import { GuestRoute, ProtectedRoute } from '@/components/ProtectedRoute';
import { PageSpinner } from '@/components/ui/Spinner';

const LoginPage          = lazy(() => import('@/features/auth/LoginPage'));
const NotFoundPage       = lazy(() => import('@/pages/NotFoundPage'));
const ReportsPage        = lazy(() => import('@/pages/ReportsPage'));
const ChatPage           = lazy(() => import('@/pages/ChatPage'));
const AdminSettingsPage  = lazy(() => import('@/pages/AdminSettingsPage'));
const AdminDashboardPage = lazy(() => import('@/pages/AdminDashboardPage'));
const HomeDashboard      = lazy(() => import('@/pages/HomeDashboard'));
const HelpPage           = lazy(() => import('@/pages/HelpPage'));
const UsersPage          = lazy(() => import('@/features/users/UsersPage'));
const ProjectsPage       = lazy(() => import('@/features/projects/ProjectsPage'));
const ProjectFormPage    = lazy(() => import('@/features/projects/ProjectFormPage'));
const ProjectDetailPage  = lazy(() => import('@/features/projects/ProjectDetailPage'));
const DrawingViewerPage  = lazy(() => import('@/features/projects/DrawingViewerPage'));
const ValidationHomePage    = lazy(() => import('@/features/validation/ValidationHomePage'));
const ProjectValidationPage = lazy(() => import('@/features/validation/ProjectValidationPage'));
const ObservationsHomePage  = lazy(() =>
  import('@/features/site/ObservationsPage').then((m) => ({ default: m.ObservationsHomePage })),
);
const ProjectObservationsPage = lazy(() => import('@/features/site/ObservationsPage'));
const CapturePage        = lazy(() => import('@/pages/CapturePage'));
const ObservationDetailPage = lazy(() => import('@/features/site/ObservationDetailPage'));
const NotificationsPage      = lazy(() => import('@/features/notifications/NotificationsPage'));

const s = (node: React.ReactNode) => <Suspense fallback={<PageSpinner />}>{node}</Suspense>;

export const router = createBrowserRouter([
  {
    element: <GuestRoute />,
    children: [{ path: '/login', element: s(<LoginPage />) }],
  },
  {
    element: <ProtectedRoute />,
    children: [
      {
        element: <AppShell />,
        children: [
          { index: true,                                           element: s(<HomeDashboard />) },
          { path: 'capture',                                       element: s(<CapturePage />) },
          { path: 'observations',                                  element: s(<ObservationsHomePage />) },
          { path: 'observations/:projectId/new',                   element: s(<CapturePage />) },
          { path: 'observations/:projectId',                       element: s(<ProjectObservationsPage />) },
          { path: 'observations/:projectId/o/:obsId',              element: s(<ObservationDetailPage />) },
          { path: 'site-queries',                                  element: <Navigate to="/observations" replace /> },
          { path: 'site-queries/:projectId',                       element: <Navigate to="/observations" replace /> },
          { path: 'reports',                                       element: s(<ReportsPage />) },
          { path: 'chat',                                          element: s(<ChatPage />) },
          { path: 'projects',                                      element: s(<ProjectsPage />) },
          { path: 'projects/new',                                  element: s(<ProjectFormPage />) },
          { path: 'projects/:projectId',                           element: s(<ProjectDetailPage />) },
          { path: 'projects/:projectId/edit',                      element: s(<ProjectFormPage />) },
          { path: 'projects/:projectId/drawings/:drawingId',       element: s(<DrawingViewerPage />) },
          { path: 'validations',                                   element: s(<ValidationHomePage />) },
          { path: 'validations/:projectId',                        element: s(<ProjectValidationPage />) },
          { path: 'notifications',                                 element: s(<NotificationsPage />) },
          { path: 'help',                                          element: s(<HelpPage />) },
          { path: 'admin',                                         element: s(<AdminDashboardPage />) },
          { path: 'admin/users',                                   element: s(<UsersPage />) },
          { path: 'admin/settings',                                element: s(<AdminSettingsPage />) },
          { path: '*',                                             element: s(<NotFoundPage />) },
        ],
      },
    ],
  },
]);
